import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import snakeCase from 'lodash/snakeCase';
import pickBy from 'lodash/pickBy';
import {
  PRODUCTS_INITIAL_TYPE_ID,
  FETCH_ADS_START,
  FETCH_ADS_SUCCESS,
  FETCH_ADS_FAILED,
  FETCH_ADS_COMPLETED,
  SET_SEARCH_PARAMS,
  CLICKOUT_START,
  CLICKOUT_SUCCESS,
  FETCH_AUTOCOMPLETE_SUCCESS,
  FETCH_AUTOCOMPLETE_FAILED,
  CLICKOUT_FAILED,
  REMOVE_GEO_ID,
  ACTIVATE_USER_SEARCH_INTERACTION,
  SET_OR_INCREMENT_AD_VISIT_COUNTER,
  SET_ADS_VISIT_COUNTER,
  HIDE_RELATED_ADS_POPUP,
  SHOW_RELATED_ADS_POPUP,
  ENABLE_RETRACK_AD_IMPRESSIONS,
  SET_SEARCH_PARAMS_PAGE,
  INITIALIZE_TYPE_ID,
  HOMES_INITIAL_TYPE_ID,
  DEFAULT_INITIAL_TYPE_ID,
  SET_SHOULD_RESET_WHERE,
} from './constants';
import { VERTICAL_NAMES } from '../common/constants/common';
import defaultState from '../state';
import {
  transformArrayFiltersToObjectFilters,
  shouldRemoveGeoId,
  getWhatByVertical,
  getWhatAccentedByVertical,
} from '../utils/searchParameters';
import { getGeoId } from './utils';
import { dirify } from '../utils/dirify';
import { FILTERS as API_LISTING_FILTERS } from '../sdk/search/Api';
import {
  ACTIVATE_USER_FILTER_INTERACTION,
  SHOW_SEARCH_FILTERS,
  HIDE_SEARCH_FILTERS,
} from './components/Filters/shared/constants';

const serpInitialState = get(defaultState, 'serp');

export default (state = serpInitialState, action) => {
  switch (action.type) {
    case FETCH_ADS_START:
      return {
        ...state,
        ads: [],
        isFetching: true,
        error: null,
        shouldRetrackAdImpressions: false,
        shouldResetWhere: false,
        didYouMeanWhat: '',
      };

    case FETCH_ADS_SUCCESS: {
      const { alternativeAds, similarContentAds } = action.data.serp;
      const { totalCount, totalPremiumCount, fresh, facets } = action.data.serp.meta;
      const freshParsed = Object.keys(fresh).reduce((acc, curr) => {
        acc[snakeCase(curr)] = fresh[curr];
        return acc;
      }, {});

      let count = totalCount;
      const isSemUrl = get(state, ['searchParameters', 'isSemUrl']);

      if (isSemUrl) {
        // we assign premium ads to the total count because on SEM landings
        // there are no organic ads and count will be equal to 0, this way
        // the paid ads count becomes the ads count
        if (totalPremiumCount > 0) {
          count += totalPremiumCount;
        }
        if (count === 0) {
          count = alternativeAds.length;
        }
      }

      const similarContentAdsCount = similarContentAds ? similarContentAds.length : 0;

      const adsMeta = { fresh: freshParsed };
      const { pagination: links } = action.data.meta || {};
      let newState = state;

      if (action.data.keyword) {
        const keywordsData = action.data.keyword;
        const whatAccented = get(state, ['searchParameters', 'whatAcceted']);
        const keywordsWhat = keywordsData.searchTerms.what;
        const keywordsWhere = keywordsData.searchTerms.where;
        const didYouMeanWhat = keywordsData.searchTerms.didYouMeanWhat;
        const whatSearch = keywordsData.searchTerms.whatSearch;
        const {
          title,
          description,
          filters: { auto },
        } = action.data.meta;
        const verticalName = get(keywordsData, ['verticalProperties', 'verticalName']);
        const carsMake = get(keywordsData, ['verticalProperties', 'cars', 'make']);
        const carsModel = get(keywordsData, ['verticalProperties', 'cars', 'model']);

        const adsFiltersArray = Object.keys(auto).reduce((acc, filterName) => {
          const filterNameSnakeCase = snakeCase(filterName);
          if (API_LISTING_FILTERS[filterNameSnakeCase]) {
            acc.push({
              name: filterNameSnakeCase,
              value: auto[filterName],
            });
          }

          return acc;
        }, []);

        const location = pickBy(
          {
            region: keywordsData.location.region,
            city: keywordsData.location.city,
            city_area: keywordsData.location.cityArea,
          },
          attribute => !isEmpty(attribute)
        );

        const interlinking = keywordsData.interlinking;
        const adsFilters = transformArrayFiltersToObjectFilters(adsFiltersArray);

        const stateGeoId = get(state, ['searchParameters', 'geoId']);
        const ignoreGeoId = get(state, ['osm', 'ignoreGeoId']);
        const geoId = getGeoId(adsFiltersArray, stateGeoId, ignoreGeoId);

        const currentIsUserFilterInteraction = get(state, [
          'searchParameters',
          'isUserFilterInteraction',
        ]);

        const newOsm = !Array.isArray(keywordsData.geoOsm)
          ? newState.osm
          : {
              ...newState.osm,
              items: keywordsData.geoOsm.map(osmItem => ({
                id: osmItem.id,
                name: osmItem.name,
                visible: osmItem.visible,
              })),
            };

        newState = {
          ...newState,
          title,
          location,
          description,
          interlinking,
          didYouMeanWhat,
          error: null,
          redirectWhat: keywordsData.redirectWhat ? dirify(keywordsData.redirectWhat) : null,
          redirectWhere: keywordsData.redirectWhere ? dirify(keywordsData.redirectWhere) : null,
          searchParameters: {
            ...newState.searchParameters,
            what_d: getWhatByVertical(verticalName, { what: keywordsWhat, whatSearch }),
            whatAccented: getWhatAccentedByVertical(verticalName, {
              whatAccented,
              what: keywordsWhat,
              where: keywordsWhere,
            }),
            where: keywordsWhere,
            whatSearch: whatSearch || null,
            geoId: geoId || null,
            isUserFilterInteraction: currentIsUserFilterInteraction || false,
            carsMake,
            carsModel,
            filters: { ...get(state, ['searchParameters', 'filters']), ...adsFilters },
          },
          osm: newOsm,
        };
      }

      if (count === 0) {
        return {
          ...newState,
          isFetching: false,
          ads: [],
          adsMeta: {},
          links: {},
          facets: {},
          interlinking: { selected_seo_links: [] },
          count: 0,
          similarContentAdsCount: 0,
          title: '',
          description: '',
          didYouMeanWhat: '',
          error: null,
        };
      }

      return {
        ...newState,
        isFetching: false,
        ads: action.data.data,
        adsMeta,
        links,
        facets: facets || {},
        count,
        similarContentAdsCount,
        error: null,
      };
    }

    case FETCH_ADS_FAILED: {
      return {
        ...state,
        count: 0,
        isFetching: false,
        title: '',
        description: '',
        error: action.error,
      };
    }

    case FETCH_ADS_COMPLETED: {
      let newState = state;

      if (action.data && action.data.keyword) {
        const keywordsData = action.data.keyword;
        const keywordsWhat = keywordsData.searchTerms.what;
        const whatSearch = keywordsData.searchTerms.whatSearch;
        const verticalName = get(keywordsData, ['verticalProperties', 'verticalName']);

        newState = {
          ...newState,
          searchParameters: {
            ...newState.searchParameters,
            what_d: getWhatByVertical(verticalName, { what: keywordsWhat, whatSearch }),
          },
        };
      }

      return newState;
    }

    case SET_SEARCH_PARAMS: {
      const { data } = action;
      const currentSearchParametersState = state.searchParameters;
      const shouldRemoveGeoIdResult = shouldRemoveGeoId(
        data.filters,
        get(currentSearchParametersState, 'isUserSearchInteraction'),
        get(state, ['osm', 'ignoreGeoId'])
      );

      return {
        ...state,
        searchParameters: {
          ...data,
          type_id: data.type_id || currentSearchParametersState.type_id,
          isUserSearchInteraction: get(currentSearchParametersState, 'isUserSearchInteraction'),
          isUserFilterInteraction: get(currentSearchParametersState, 'isUserFilterInteraction'),
          whatOriginalTyped: data.what_d,
          geoId: shouldRemoveGeoIdResult ? null : data.geoId,
        },
      };
    }

    case CLICKOUT_START:
      return { ...state, isFetching: true, error: null };

    case CLICKOUT_SUCCESS:
      return { ...state, isFetching: false, error: null };

    case CLICKOUT_FAILED:
      return { ...state, isFetching: false, error: action.error };

    case FETCH_AUTOCOMPLETE_SUCCESS: {
      return { ...state, autoComplete: action.data };
    }

    case FETCH_AUTOCOMPLETE_FAILED:
      return { ...state, autoComplete: [] };

    case REMOVE_GEO_ID:
      return {
        ...state,
        searchParameters: { ...state.searchParameters, geoId: false },
        osm: { ...state.osm, ignoreGeoId: true },
      };

    case ACTIVATE_USER_SEARCH_INTERACTION:
      return {
        ...state,
        searchParameters: {
          ...state.searchParameters,
          isUserSearchInteraction: true,
        },
      };

    case ACTIVATE_USER_FILTER_INTERACTION:
      return {
        ...state,
        searchParameters: {
          ...state.searchParameters,
          isUserFilterInteraction: true,
        },
      };

    case SET_OR_INCREMENT_AD_VISIT_COUNTER: {
      const adsVisitCounter = get(state, 'adsVisitCounter');
      const adCounterToUpdate = get(adsVisitCounter, action.payload, { count: 0 });
      return {
        ...state,
        adsVisitCounter: {
          ...adsVisitCounter,
          [action.payload]: { ...adCounterToUpdate, count: get(adCounterToUpdate, 'count') + 1 },
        },
      };
    }

    case SET_ADS_VISIT_COUNTER:
      return { ...state, adsVisitCounter: action.payload ? action.payload : {} };

    case SHOW_RELATED_ADS_POPUP:
      return {
        ...state,
        relatedAdsPopup: {
          ...state.relatedAdsPopup,
          isShow: true,
          redirectUrl: action.payload.redirectUrl,
        },
      };

    case HIDE_RELATED_ADS_POPUP:
      return {
        ...state,
        relatedAdsPopup: {
          ...state.relatedAdsPopup,
          isShow: false,
          redirectUrl: '',
        },
      };

    case SHOW_SEARCH_FILTERS:
      return { ...state, showingFilters: true };

    case HIDE_SEARCH_FILTERS:
      return { ...state, showingFilters: false };

    case ENABLE_RETRACK_AD_IMPRESSIONS:
      return { ...state, shouldRetrackAdImpressions: true };

    case SET_SEARCH_PARAMS_PAGE:
      return {
        ...state,
        searchParameters: {
          ...state.searchParameters,
          page: Number(action.payload),
        },
      };

    case INITIALIZE_TYPE_ID:
      switch (action.payload) {
        case VERTICAL_NAMES.products:
          return {
            ...state,
            searchParameters: {
              ...state.searchParameters,
              type_id: PRODUCTS_INITIAL_TYPE_ID,
            },
          };
        case VERTICAL_NAMES.homes:
          return {
            ...state,
            searchParameters: {
              ...state.searchParameters,
              type_id: HOMES_INITIAL_TYPE_ID,
            },
          };
        default:
          return {
            ...state,
            searchParameters: {
              ...state.searchParameters,
              type_id: DEFAULT_INITIAL_TYPE_ID,
            },
          };
      }
    case SET_SHOULD_RESET_WHERE: {
      return { ...state, shouldResetWhere: true };
    }
    default:
      return state;
  }
};



// WEBPACK FOOTER //
// ./app/Serp/reducer.js